import { useEffect, useState } from "react";

import { CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { collection, getDocs } from "firebase/firestore";

import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";

const toAmount = value => {
  const amount = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatCurrency = value => {
  return new Intl.NumberFormat("fr-FR", {
    currency: "EUR",
    style: "currency"
  }).format(value);
};

const GlobalStatistics = () => {
  const { currentUser } = useAuth();
  const [globalStats, setGlobalStats] = useState({
    totalIncomeAllYears: 0,
    totalInvoicedAllYears: 0,
    averageMonthlyIncome: 0,
    remainingOrders: 0,
    pendingTasks: 0,
    totalClients: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGlobalStatistics = async () => {
      setIsLoading(true);
      let totalIncomeAllYears = 0;
      let totalInvoicedAllYears = 0;
      let remainingOrders = 0;
      let pendingTasks = 0;
      const today = new Date();
      let minDate = new Date();  // Initialiser avec la date actuelle
      let maxDate = new Date(0); // Initialiser avec la plus ancienne date possible

      try {
        const ordersSnapshot = await getDocs(
          collection(db, `users/${currentUser.uid}/orders`)
        );

        ordersSnapshot.forEach(doc => {
          const order = doc.data();
          const orderDate = new Date(order.selectedDate);

          // Calcul pour les prestations restantes
          if (orderDate > today && !order.orderIsCanceled) {
            remainingOrders += 1;
          }

          // Total encaissé et total facturé toutes années confondues
          // Calcul du revenu total encaissé pour toutes les années
          if (order.paymentDetails) {
            order.paymentDetails.forEach(payment => {
              const paymentDate = new Date(payment.paymentDate);
              if (payment.isPaid) {
                totalIncomeAllYears += toAmount(payment.value);

                if (!Number.isNaN(paymentDate.getTime())) {
                  // Mettre à jour les dates min et max pour les paiements
                  minDate = paymentDate < minDate ? paymentDate : minDate;
                  maxDate = paymentDate > maxDate ? paymentDate : maxDate;
                }
              }
            });
          }

          if (order.orderIsConfirmed) {
            totalInvoicedAllYears += toAmount(order.totalPrice);
          }

          if (order.workflow?.tasks) {
            pendingTasks += order.workflow.tasks.filter(task => !task.isDone).length;
          }
        });

        // Calcul du nombre de mois entre minDate et maxDate
        const hasPaidDateRange =
          !Number.isNaN(minDate.getTime()) &&
          !Number.isNaN(maxDate.getTime()) &&
          maxDate >= minDate;

        const totalMonths = hasPaidDateRange
          ? (maxDate.getFullYear() - minDate.getFullYear()) * 12 +
            (maxDate.getMonth() - minDate.getMonth() + 1)
          : 0; // +1 pour inclure le mois de fin

        const clientsSnapshot = await getDocs(
          collection(db, `users/${currentUser.uid}/customers`)
        );
        const totalClients = clientsSnapshot.size;

        const averageMonthlyIncome = totalMonths > 0 ? totalIncomeAllYears / totalMonths : 0;

        setGlobalStats({
          totalIncomeAllYears,
          totalInvoicedAllYears,
          averageMonthlyIncome,
          remainingOrders,
          pendingTasks,
          totalClients
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur lors de la récupération des statistiques globales :", error);
      }

      setIsLoading(false);
    };

    fetchGlobalStatistics();
  }, [currentUser]);

  return isLoading ? (
    <CircularProgress style={{ margin: "auto" }} />
  ) : (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Prestations restantes
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {globalStats.remainingOrders}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Tâches non traitées
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {globalStats.pendingTasks}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Total clients
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {globalStats.totalClients}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Revenu total (toutes années)
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {formatCurrency(globalStats.totalIncomeAllYears)} / {formatCurrency(globalStats.totalInvoicedAllYears)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            Revenu moyen mensuel global
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {formatCurrency(globalStats.averageMonthlyIncome)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default GlobalStatistics;
